import type { Project } from "@/types/project";

export const PROJECTS: Project[] = [
  {
    id: 6,
    slug: "anna-b",
    title: "Anna B",
    category: "Music Artist / Full Platform",
    description:
      "A neon-soaked artist platform for Anna B — a Louisville, Kentucky-born rapper and R&B singer making her way in Atlanta. A retro-electronics website where a vintage boombox is the music player and a CRT TV plays the videos, backed by a full self-serve CMS with events, galleries, tracks, and built-in analytics. Part EPK, part fan hub, all attitude.",
    color: "#ff2d6f",
    image: "/images/annab/screen-1.png",
    url: "https://darealannab.com",
    stats: [
      { label: "site sections", value: "7" },
      { label: "admin modules", value: "6" },
      { label: "integrations", value: "Spotify + YouTube" },
      { label: "status", value: "Live" },
    ],
    caseStudy: {
      challenge:
        "Anna B is a rapper and R&B artist born and raised in Louisville, Kentucky, now making her way in Atlanta's music scene. She writes all her own music — every bar, every hook, every melody — and models on the side. She needed more than a link-in-bio: a home base bold enough to match her sound, one place to show her music, videos, shows, and modeling, and something she could run herself — updating her bio, dropping new tracks, and posting shows without paying a developer every time. Generic artist templates felt flat, and DIY builders couldn't deliver the interactive, unmistakably-her experience she was after.",
      approach:
        "We hand-coded a neon-drenched artist platform around one idea: the site should feel like her music. The hero is a photoreal vintage boombox that actually plays — press play and the speakers glow while her top tracks spin, pulled live from Spotify. The retro-electronics motif runs throughout: music videos live inside a CRT television with a channel dial and a scrollable video reel (BAD, Like Dat, Power, One Night, Pressure), section navigation glows down the side as neon dots, and a hand-lettered 'Connect with Anna B' anchors the link hub. The 'Atlanta — Louisville' wordmark ties both cities into her identity. Beyond the music, the site doubles as a model portfolio — shoots grouped by photographer and gallery ('At the Junkyard,' 'Cute in Curlers') — an events board with full show flyers, and a connect page that unites her Instagram, TikTok, YouTube, Linktree, and email with an embedded Spotify player. Behind a login sits a full custom CMS: an About editor (artist bio, model info, socials, links), plus Events, Videos, and Tracks managers and a Portfolio manager that organizes galleries by creator with publish controls — all fronted by a Dashboard with built-in analytics showing views over 7 and 30 days, unique visitors, and top pages. Anna B runs the entire site herself. It's built mobile-first and fully responsive, for the way her fans actually find her — on their phones.",
      results:
        "Anna B launched with a website that looks and moves like nothing else in her lane — an interactive, retro-futurist platform that turns a casual listener into a fan and hands promoters, bookers, and collaborators everything in one link. The built-in CMS means she updates her own bio, drops new tracks, posts shows, and publishes photo galleries without waiting on anyone, and the analytics dashboard shows her exactly what's landing. Music, videos, modeling, and live shows — all under darealannab.com, all hers to run.",
      techStack: [
        "JavaScript",
        "HTML5",
        "CSS3",
        "Supabase",
        "Custom CMS",
        "Spotify Integration",
        "YouTube Embeds",
        "Analytics Dashboard",
        "Auth System",
        "Responsive Design",
      ],
      gallery: [
        "/images/annab/screen-1.png",
        "/images/annab/screen-2.png",
        "/images/annab/screen-3.png",
        "/images/annab/screen-4.png",
        "/images/annab/screen-5.png",
        "/images/annab/screen-6.png",
        "/images/annab/screen-7.png",
        "/images/annab/screen-8.png",
      ],
      timeline: "Custom Build",
    },
  },
  {
    id: 0,
    slug: "grace-grit",
    title: "Grace & Grit",
    category: "Nonprofit / Full Platform",
    description:
      "A complete digital platform for a faith-driven travel nonprofit — from a cinematic public website to a full custom CRM, AI-powered trip logistics, Stripe-integrated finance dashboard, legal document generation, and content management. This is the build that shows what The Odd Stack can really do.",
    color: "#4a7c59",
    image: "/images/grace-grit/screen-1.png",
    url: "https://grace-grit.org",
    featured: true,
    featureHighlights: [
      {
        icon: "📊",
        title: "Custom CRM",
        description:
          "Full contact management with pipeline tracking, status labels, source tracking, and quick actions. Every donor, traveler, and supporter in one place.",
      },
      {
        icon: "✈️",
        title: "Trip Advisor",
        description:
          "A Kanban-style trip management board that tracks journeys from Planning through Gathering Info, Booked, Traveling, and Completed. Each trip card tracks travelers, passport alerts, and missing documents.",
      },
      {
        icon: "🤖",
        title: "AI Journey Planner",
        description:
          "An AI-powered logistics engine that analyzes traveler locations and generates optimal flight strategies — hub meetups, direct routes, carrier suggestions, and return journeys. It even recommends airports and timing.",
      },
      {
        icon: "📄",
        title: "Legal Document Generator",
        description:
          "Auto-generates travel liability waivers and release forms with participant data pre-filled. Supports reusable templates, live preview, print, copy, and DocuSign-ready export.",
      },
      {
        icon: "💰",
        title: "Finance Dashboard",
        description:
          "Stripe-integrated donation tracking with fully configurable widget grid — total donations, MRR, donor LTV, retention rates, growth trends, and projections. Drag-and-drop layout with toggle controls for each metric.",
      },
      {
        icon: "🌍",
        title: "Adventure Management",
        description:
          "Upload and manage destination videos, photo galleries, and trip content displayed on the public site. Full CRUD with thumbnails, sort ordering, and regional organization.",
      },
    ],
    stats: [
      { label: "admin modules", value: "15+" },
      { label: "AI features", value: "3" },
      { label: "integrations", value: "Stripe + AI" },
      { label: "status", value: "Live" },
    ],
    caseStudy: {
      challenge:
        "Grace & Grit is a faith-driven nonprofit that organizes service-based travel experiences — blending adventure, community impact, and personal reflection across destinations like the Philippines, Costa Rica, Iceland, and Brazil. The founder, Sharon Murphy, needed far more than a website. She needed a platform that could manage contacts and donors, plan complex multi-traveler international trips, generate legal documents, track donations with real financial analytics, manage content, and present it all through a beautiful public-facing site that inspires people to join the mission.",
      approach:
        "We built the entire platform from scratch — no templates, no WordPress, no third-party CRM. The public site features a cinematic hero with full-screen video backgrounds, Sharon's personal story with editorial photography, an Adventures section with video galleries organized by destination, tiered donation impact cards ($25 care packages, $50 village meals, $250 community support), and a journey signup flow. Behind the login sits a full admin panel with 15+ modules: a CRM dashboard with pipeline visualization, a Trip Advisor with Kanban board management, an AI-powered Journey Planner that calculates optimal travel logistics for groups departing from different cities, a legal document generator with live preview and template reuse, a Stripe-synced Finance Dashboard with a configurable widget grid showing 16+ financial metrics (MRR, donor LTV, retention, growth trends, projections), waiver management, questionnaire builder, email tools, gallery management, adventure video uploads, social link management, and site-wide settings. Every module was designed to work together — contacts flow into trips, trips generate waivers, donations sync from Stripe, and the finance dashboard updates in real time.",
      results:
        "Grace & Grit launched with a platform that rivals tools nonprofits pay thousands per month for — except this one was built custom, exactly how Sharon needed it. The AI Journey Planner alone saves hours of manual flight research per trip. The finance dashboard gives board-level visibility into donation health without spreadsheets. The waiver generator eliminated the need for third-party legal document services. And the public site converts visitors into supporters with a clear emotional journey from story to impact to action.",
      techStack: [
        "Next.js",
        "Supabase",
        "Stripe API",
        "AI / Claude API",
        "Custom CRM",
        "Document Generation",
        "Video Management",
        "Kanban UI",
        "Finance Analytics",
        "Auth System",
      ],
      gallery: [
        "/images/grace-grit/screen-1.png",
        "/images/grace-grit/screen-2.png",
        "/images/grace-grit/screen-3.png",
        "/images/grace-grit/screen-4.png",
        "/images/grace-grit/screen-5.png",
        "/images/grace-grit/screen-6.png",
        "/images/grace-grit/screen-7.png",
        "/images/grace-grit/screen-8.png",
        "/images/grace-grit/screen-9.png",
        "/images/grace-grit/screen-10.png",
        "/images/grace-grit/screen-11.png",
        "/images/grace-grit/screen-12.png",
        "/images/grace-grit/screen-13.png",
        "/images/grace-grit/screen-14.png",
        "/images/grace-grit/screen-15.png",
      ],
      timeline: "Ongoing Partnership",
    },
    mobileApp: {
      name: "Tala",
      tagline: "Grace & Grit Travel Companion",
      description:
        "Tala is the traveler-facing mobile app that connects directly to the Grace & Grit admin platform. When a trip coordinator creates a trip in Trip Advisor and adds travelers, those travelers get access to Tala — where they can view trip details, submit passport and emergency contact info, sign legal waivers, check in daily with their guide, see fellow travelers, and share their live GPS location.",
      screens: [
        {
          src: "/images/tala/screen-1.png",
          alt: "Tala login",
          title: "Secure Email Login",
          description: "Travelers sign in with a one-time code sent to their email — no passwords to remember. Accounts are created by the trip coordinator through the admin CRM, so only verified travelers get access.",
        },
        {
          src: "/images/tala/screen-2.png",
          alt: "Trip Details",
          title: "Trip Details & Countdown",
          description: "Each trip shows a countdown to departure, action items that need attention (missing passport info, unsigned waivers, incomplete documents), dates, trip type, and quick access to travel advisories and fellow travelers. Synced directly from Trip Advisor.",
        },
        {
          src: "/images/tala/screen-3.png",
          alt: "Passport & Identity",
          title: "Passport & Identity Documents",
          description: "Travelers fill in passport number, nationality, date of birth, and expiry — the same fields that trigger alerts in the admin's Trip Advisor when missing. Completion tracking shows progress (1 of 9 fields), and an Apple Wallet tip helps with digital ID setup.",
        },
        {
          src: "/images/tala/screen-4.png",
          alt: "Emergency & Health",
          title: "Emergency Contact & Health Info",
          description: "Emergency contact details, dietary needs, and medical notes — all synced back to the admin dashboard so trip coordinators have critical information before departure. This is the data that feeds the passport alerts in Trip Advisor.",
        },
        {
          src: "/images/tala/screen-5.png",
          alt: "Fellow Travelers",
          title: "Fellow Travelers",
          description: "See who else is on your trip with name, email, and phone number. Tap to call, text, or email directly. Travelers can connect before the trip even starts — building community before they meet in person.",
        },
        {
          src: "/images/tala/screen-6.png",
          alt: "Waivers & Documents",
          title: "Waivers & Documents",
          description: "Legal waivers generated by the admin's Legal Document Generator appear here as required documents. Each waiver shows its status (Pending/Signed) with a 'Review & Sign in App' button — no paper, no PDF, no printing.",
        },
        {
          src: "/images/tala/screen-7.png",
          alt: "Sign Waiver",
          title: "In-App Waiver Signing",
          description: "Full legal document review with multi-page scroll, formal sections, and a 'Continue to Sign' flow. The signed waiver syncs back to the admin's Legal Docs module — the coordinator sees completion in real time without chasing signatures.",
        },
        {
          src: "/images/tala/screen-8.png",
          alt: "Check In",
          title: "Daily Check-In",
          description: "Travelers report their status each day — All Good, Need Help, or Emergency — with an optional message to their guide. Trip coordinators get real-time safety awareness across the entire group from the admin dashboard.",
        },
        {
          src: "/images/tala/screen-9.png",
          alt: "Share Location",
          title: "Live Location Sharing",
          description: "GPS sharing with guide and fellow travelers on a live map. Last Known Locations show where everyone was last seen. In unfamiliar destinations, this keeps the group connected and gives coordinators peace of mind.",
        },
      ],
    },
  },
  {
    id: 1,
    slug: "everyday-goddess",
    title: "Everyday Goddess",
    category: "Photography / Brand Experience",
    description:
      "A full-service photography brand website with tiered investment packages, online booking, client galleries, and a design that makes every woman feel like the goddess she already is.",
    color: "#c026d3",
    image: "/images/everydaygoddess/screen-2.png",
    url: "https://everydaygoddessphoto.com",
    stats: [
      { label: "pages", value: "8+" },
      { label: "packages", value: "3 Tiers" },
      { label: "bookings", value: "Live" },
    ],
    caseStudy: {
      challenge:
        "Everyday Goddess needed more than a portfolio — it needed a full brand experience that communicated luxury, empowerment, and transformation from the first scroll. The existing web presence didn't match the quality of the photography or the emotional depth of the client experience.",
      approach:
        "We designed and hand-coded a complete brand site with a warm, editorial aesthetic — rich typography, soft gradients, and intentional whitespace that lets the photography breathe. The investment page presents three distinct session tiers (Awakening, Radiance, Divine) with clear pricing and feature breakdowns. A private client gallery system, online booking flow, and styling guidance section round out the experience. Every detail — from the navigation to the pricing cards — was built to convert visitors into clients who feel confident investing in themselves.",
      results:
        "The site launched with integrated booking, a client login portal for private gallery access, and a pricing structure that increased average session value. The investment page alone reduced back-and-forth inquiry emails by giving clients everything they need to choose their experience upfront.",
      techStack: [
        "Next.js",
        "Custom CSS",
        "Client Portal",
        "Online Booking",
        "Gallery System",
      ],
      gallery: [],
      timeline: "Custom Build",
    },
  },
  {
    id: 2,
    slug: "goddess-unfiltered",
    title: "Goddess Unfiltered",
    category: "Media / Podcast Platform",
    description:
      "A podcast and media platform launching Spring 2025 — blending empowerment photography with raw, unfiltered storytelling. Part photoshoot, part reveal, all real.",
    color: "#ec4899",
    image: "/images/goddessunfiltered/screen-1.png",
    url: "https://goddessunfilteredpodcast.com",
    stats: [
      { label: "sections", value: "9" },
      { label: "launch", value: "Spring '25" },
      { label: "CTA flows", value: "3" },
    ],
    caseStudy: {
      challenge:
        "Goddess Unfiltered is a two-part experience — a powerful empowerment photoshoot followed by a heartfelt on-camera reveal during the podcast episode. The website needed to capture that emotional arc while also serving as a functional media hub with episode pages, guest applications, sponsorship info, and partner showcases.",
      approach:
        "We built a visually striking platform with a soft, ethereal gradient aesthetic that transitions between warmth and editorial polish. The hero section leads with the show's tagline — 'Real Women, Real Stories, Real Transformation' — and immediately funnels visitors toward two actions: becoming a guest or exploring the experience. The site includes dedicated sections for episodes, the behind-the-scenes photoshoot process, partner logos, sponsorship tiers, and a 'Be a Guest' application flow. An animated audio waveform motif ties the podcast identity throughout the design.",
      results:
        "The site is live and building anticipation ahead of the Spring 2025 launch. The guest application pipeline is active, sponsorship inquiries are flowing, and the visual identity has become the foundation for the show's social media presence and promotional materials.",
      techStack: [
        "Next.js",
        "Custom Animations",
        "Gradient System",
        "Application Forms",
        "Responsive Design",
      ],
      gallery: [],
      timeline: "Custom Build",
    },
  },
  {
    id: 3,
    slug: "rlb-photo",
    title: "RLB Photo",
    category: "Photography / Portfolio",
    description:
      "A bold, dark, magazine-inspired portfolio for a glamour and model photographer — built to hit hard, showcase covers, and let the work do the talking.",
    color: "#ffffff",
    image: "/images/rlbphoto/screen-7.png",
    url: "https://rlbphoto.netlify.app/",
    stats: [
      { label: "aesthetic", value: "Editorial" },
      { label: "galleries", value: "Full" },
      { label: "covers", value: "Featured" },
    ],
    caseStudy: {
      challenge:
        "RLB Photo needed a website that matched the intensity of the work — high-end glamour and model photography with published magazine covers and editorial spreads. Template sites felt cheap. The photographer's brand is dark, cinematic, and unapologetically bold — the website had to be the same.",
      approach:
        "We went full editorial — a dark, immersive design built around a magazine-collage hero section that showcases published covers and spreads in a dynamic, overlapping layout. The skull logo sits center stage, flanked by real magazine tearsheets. Navigation is minimal and confident. The portfolio section uses full-bleed imagery with smooth transitions. A dedicated covers page highlights published work, and the rates page presents booking info with the same cinematic energy as the rest of the site. Every pixel is intentional — no fluff, no filler, just the work.",
      results:
        "The site immediately elevated the photographer's professional presence. The magazine-cover hero section became a signature visual element that gets referenced by clients and fellow photographers. Portfolio inquiries increased, and the rates page streamlined the booking conversation by setting expectations before the first email.",
      techStack: [
        "Next.js",
        "Custom CSS",
        "Magazine Layout",
        "Full-Bleed Galleries",
        "Dark Theme",
      ],
      gallery: [],
      timeline: "Custom Build",
    },
  },
  {
    id: 5,
    slug: "applecart",
    title: "AppleCart & CartKeeper Pro",
    category: "Copyright Protection / Full Platform",
    description:
      "A DMCA takedown and copyright enforcement platform — from a cinematic public site with pricing tiers and a free protection score tool, to CartKeeper Pro: a full-stack automation engine with AI-powered web harvesting, network intelligence mapping, Google deindexing, automated legal notices, Stripe billing, and a URL filing system tracking 6,000+ infringing links across the internet.",
    color: "#e53e3e",
    image: "/images/applecart-cartkeeper/screen-1.png",
    url: "https://applecartdmca.com",
    featured: false,
    featureHighlights: [
      {
        icon: "🦋",
        title: "Fiona AI",
        description:
          "An AI agent at the heart of CartKeeper — real-time content scanning, pattern recognition, automated legal compliance, and predictive threat analysis.",
      },
      {
        icon: "🕷️",
        title: "Harvester",
        description:
          "AI-powered web scraping and URL discovery with deep crawl, forum crawl, and single-page modes. Targets common offender sites automatically.",
      },
      {
        icon: "🔍",
        title: "Spider Search",
        description:
          "Claude AI-powered intelligent search that scans 32+ leak sites, forums, and aggregators to find stolen content — no Google CSE needed.",
      },
      {
        icon: "🌐",
        title: "SAINT Network Intelligence",
        description:
          "Infrastructure mapping that traces ASNs, IPs, servers, domains, and contacts across hosting networks to identify the full chain behind infringing sites.",
      },
      {
        icon: "📧",
        title: "Automated Legal Notices",
        description:
          "Template-driven DMCA and trademark notice generator with HTML editing, AI generation, variable insertion, and multi-stage escalation workflows.",
      },
      {
        icon: "🗂️",
        title: "URL Filing Cabinet",
        description:
          "Per-client URL collections with status tracking (Active, Removed, Hotlinked), one-click DMCA notice drafting, and bulk import capabilities.",
      },
    ],
    stats: [
      { label: "URLs tracked", value: "6,000+" },
      { label: "removals", value: "2,396" },
      { label: "AI tools", value: "3" },
      { label: "status", value: "Live" },
    ],
    caseStudy: {
      challenge:
        "Content creators across the internet face an overwhelming problem: their copyrighted work gets stolen, reposted, and monetized on hundreds of piracy and leak sites. Existing DMCA services are expensive, slow, and opaque. Creators needed an affordable, automated system that could find stolen content across the web, trace the hosting infrastructure behind it, generate legally compliant takedown notices, and track the entire enforcement lifecycle — from discovery to removal to Google deindexing. This was the first app Jason ever built, starting from scratch in Google's Firestore builder before discovering Gemini and eventually incorporating the Vuexy UI framework to rapidly build out the platform's extensive feature set.",
      approach:
        "We built a dual-brand platform: AppleCart as the public-facing copyright protection service with a cinematic marketing site, and CartKeeper Pro as the powerful automation engine behind it. The public site features a full-screen hero with the AppleCart butterfly identity, DMCA feature cards, a CartKeeper Pro introduction section, a 'Meet Fiona' AI showcase, client testimonials, three-tier pricing (Elite $199/mo, Elite+ $299/mo, Platinum $450/mo), and a free Content Protection Score tool that captures leads. Behind the login, CartKeeper Pro is a full command center: a dashboard tracking 6,000+ URLs with real-time removal stats, a Billing Hub synced with Stripe showing MRR, revenue trends, and subscription analytics, a Subscription Renewals manager with plan and expiry tracking, the SAINT network intelligence mapper that traces ASNs, IPs, servers, domains, and contacts across hosting infrastructure, the Harvester — an AI-powered web crawler with deep crawl, forum crawl, and single-page modes targeting common offender sites, Spider Search using Claude AI to intelligently scan 32+ leak sites and forums without Google CSE, a Google Deindexer that scans Gmail for removal confirmations and tracks deindex report status, an email template system with HTML editing, AI generation, and DMCA/trademark variable insertion, and a URL filing cabinet organizing every discovered link by client with status badges, collection grouping, and one-click DMCA notice drafting.",
      results:
        "AppleCart launched as a fully operational copyright enforcement platform serving real clients with real results — 2,396 confirmed removals from 6,096 tracked URLs. The Harvester and Spider Search tools replaced hours of manual searching with AI-powered automation. SAINT revealed hosting infrastructure that manual investigation would never uncover. The template system and auto-generated notices turned legal compliance from a bottleneck into a one-click operation. This was the project that proved building from zero — Firestore to Gemini to Vuexy — could produce a platform that competes with enterprise-level DMCA services.",
      techStack: [
        "Vue.js / Vuexy",
        "Firebase / Firestore",
        "Stripe API",
        "Claude AI",
        "Gemini AI",
        "Google Deindex API",
        "DMCA Automation",
        "Network Intelligence",
        "Web Crawling",
        "Email Templating",
      ],
      gallery: [
        "/images/applecart-cartkeeper/screen-1.png",
        "/images/applecart-cartkeeper/screen-2.png",
        "/images/applecart-cartkeeper/screen-3.png",
        "/images/applecart-cartkeeper/screen-4.png",
        "/images/applecart-cartkeeper/screen-5.png",
        "/images/applecart-cartkeeper/screen-6.png",
        "/images/applecart-cartkeeper/screen-7.png",
        "/images/applecart-cartkeeper/screen-8.png",
        "/images/applecart-cartkeeper/screen-10.png",
        "/images/applecart-cartkeeper/screen-11.png",
        "/images/applecart-cartkeeper/screen-12.png",
        "/images/applecart-cartkeeper/screen-13.png",
        "/images/applecart-cartkeeper/screen-14.png",
        "/images/applecart-cartkeeper/screen-15.png",
        "/images/applecart-cartkeeper/screen-16.png",
        "/images/applecart-cartkeeper/screen-17.png",
        "/images/applecart-cartkeeper/screen-18.png",
      ],
      timeline: "The Origin Story",
    },
    mobileApp: {
      label: "The Companion App",
      name: "ClientKeeper",
      tagline: "AppleCart, in your pocket",
      description:
        "ClientKeeper is the native iOS companion to the AppleCart platform — built with React Native and Expo on the same Supabase backend as CartKeeper Pro. It gives protected creators a real-time window into their enforcement: every tracked link, every takedown notice, every removal. And it gives operators a full mobile command center — the same SAINT-powered dashboard, client management, deindex tooling, and messaging that runs the platform, now on a phone. One login screen, two surfaces: a Client portal and an Admin console.",
      screens: [
        {
          src: "/images/clientkeeper-app/client-account.png",
          alt: "ClientKeeper — client protection dashboard",
          title: "Your Protection at a Glance",
          description:
            "Clients sign in with a one-time email code and land on a tailored dashboard — total URLs tracked, removals processed, completion rate, and notices sent. Contact details, billing, and Stripe management all live a tap away.",
        },
        {
          src: "/images/clientkeeper-app/client-content.png",
          alt: "ClientKeeper — content collections with removal progress",
          title: "Every Link, Every Removal",
          description:
            "Tracked URLs are grouped into collections by source site, each with a live removal progress bar. Clients watch their enforcement happen in real time — 100% removed reads as peace of mind.",
        },
        {
          src: "/images/clientkeeper-app/client-takedowns.png",
          alt: "ClientKeeper — takedown notices timeline",
          title: "Takedown Notices, Live",
          description:
            "A running timeline of every DMCA notice and monthly report sent on the client's behalf — recipient, date, and URL count — with full details one tap away. No more wondering whether anything is being done.",
        },
        {
          src: "/images/clientkeeper-app/client-deindex.png",
          alt: "ClientKeeper — Google deindex request",
          title: "Request Google Deindexing",
          description:
            "Clients search Google for stolen content and flag results for removal directly from their phone. Major social platforms are filtered out automatically — they have their own removal pipelines.",
        },
        {
          src: "/images/clientkeeper-app/client-loa.png",
          alt: "ClientKeeper — Letter of Authorization signing",
          title: "Sign Your Authorization",
          description:
            "The Letter of Authorization that empowers AppleCart to act as the client's agent is prepared, countersigned, and signed by the client right in the app — finger-drawn, captured, and synced back to the platform.",
        },
        {
          src: "/images/clientkeeper-app/admin-dashboard.png",
          alt: "ClientKeeper — operator admin dashboard",
          title: "An Operator Command Center",
          description:
            "Flip to the Admin console and the whole platform comes mobile: roster removal rate, total URLs, a live Removed / Active / Notices breakdown, and top clients ranked by volume — the SAINT dashboard in your hand.",
        },
        {
          src: "/images/clientkeeper-app/admin-content.png",
          alt: "ClientKeeper — admin client management",
          title: "Every Client, One Tap Away",
          description:
            "Operators browse every client and prospect with URL counts, collections, and removal stats, search instantly, and drill into any account's full filing cabinet — the URL manager, on mobile.",
        },
        {
          src: "/images/clientkeeper-app/admin-messages.png",
          alt: "ClientKeeper — admin client messaging",
          title: "Client Messaging, Built In",
          description:
            "Two-way messaging with each client, where shared links can be pushed straight into the URL tracker or queued for deindexing — turning a conversation into enforcement without leaving the thread.",
        },
      ],
    },
  },
];

export function getProjectBySlug(slug: string): Project | undefined {
  return PROJECTS.find((p) => p.slug === slug);
}

export function getAllProjectSlugs(): string[] {
  return PROJECTS.map((p) => p.slug);
}
